import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, CheckCircle, XCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(true);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      if (message.includes("expired") || message.includes("Invalid")) {
        setError(true);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Error state - invalid or expired token
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="p-8 bg-void-surface border border-white/5">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-syne text-2xl font-bold text-white mb-4">
              Invalid or Expired Link
            </h1>
            <p className="text-slate-400 mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/login">
              <Button className="w-full bg-purple hover:bg-purple-hover text-white py-6 font-bold uppercase tracking-widest text-xs">
                Back to Login
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="p-8 bg-void-surface border border-white/5">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-syne text-2xl font-bold text-white mb-4">
              Password Reset Complete!
            </h1>
            <p className="text-slate-400 mb-6">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <Link to="/login">
              <Button className="w-full bg-purple hover:bg-purple-hover text-white py-6 font-bold uppercase tracking-widest text-xs">
                Sign In
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center py-24 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img 
              src="/images/hero-image.png" 
              alt="AI Film Society" 
              className="h-12 w-12 object-contain"
            />
          </Link>
          <h1 className="font-syne text-3xl font-bold text-white mb-2">
            Create New Password
          </h1>
          <p className="text-slate-400">Enter your new password below</p>
        </div>

        <div className="p-8 bg-void-surface border border-white/5">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  data-testid="new-password"
                  className="pl-10 bg-void border-white/10 text-white placeholder-slate-500"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-2 block">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password"
                  className="pl-10 bg-void border-white/10 text-white placeholder-slate-500"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full bg-purple hover:bg-purple-hover text-white py-6 font-bold uppercase tracking-widest text-xs"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>

          <p className="text-center text-slate-400 mt-6">
            Remember your password?{" "}
            <Link to="/login" className="text-purple-light hover:text-white">
              Sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
